import React, { useState } from 'react'
import AyudaExportar from './ayuda-exportar'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts'

const NOMBRES_SERIE = {
    cerradosConRecupero: 'Con Recupero',
    cerradosSinRecupero: 'Sin Recupero'
}

// Color de resaltado de cada mitad: el de su serie, para que se vea CUÁL de las dos barras se descargará.
const RESALTE = { con: 'rgba(58, 125, 68, 0.20)', sin: 'rgba(192, 57, 43, 0.20)' }

const cantidadDe = (fila, resultado) =>
    (resultado === 'con' ? fila?.cerradosConRecupero : fila?.cerradosSinRecupero) || 0

/**
 * Cursor del tooltip: en vez de resaltar la franja completa del proveedor (que cubre las dos barras y no
 * deja claro cuál se descarga), pinta solo la mitad bajo el mouse — arriba «Con», abajo «Sin» — en el
 * color de su serie. Si esa mitad no tiene casos no se pinta: ahí un clic no descarga nada.
 * Recibe `x`, `y`, `width`, `height` de la franja (los inyecta el Tooltip al clonar el elemento).
 */
const MitadResaltada = ({ x, y, width, height, resultado, cantidad }) => {
    if (!cantidad) return null
    const arriba = resultado === 'con'
    return (
        <rect
            x={x}
            y={arriba ? y : y + height / 2}
            width={width}
            height={height / 2}
            fill={RESALTE[resultado]}
            pointerEvents='none'
        />
    )
}

/** Mitad de la franja donde está el mouse: las dos barras van una sobre otra (Con arriba, Sin abajo). */
const mitadDe = (estado) => {
    const centro = estado?.activeCoordinate?.y
    return centro === undefined || estado?.chartY === undefined || estado.chartY <= centro ? 'con' : 'sin'
}

/**
 * Cerrados con y sin recupero por proveedor.
 * Con `onExportar(responsableId, 'con' | 'sin')`, cada barra descarga los casos que representa (RG-602).
 */
const CerradosPorProveedor = ({ data = [], onExportar }) => {
    // Fila y mitad bajo el mouse; alimenta el resaltado. Los hooks van antes de cualquier `return`.
    const [foco, setFoco] = useState({ resultado: 'con', fila: null })

    if (!data.length) return <div className='text-center text-muted py-5'>Sin datos</div>

    // El alto crece con la cantidad de proveedores para que las barras no se aplasten.
    const altura = Math.max(250, data.length * 55)

    const alClic = (resultado) => (barra) => {
        const fila = barra?.payload ?? barra
        if (onExportar && fila?.responsableId) onExportar(fila.responsableId, resultado)
    }
    const cursor = onExportar ? 'pointer' : undefined

    const alMover = (estado) => {
        const fila = estado?.activePayload?.[0]?.payload
        if (!fila) return
        const resultado = mitadDe(estado)
        setFoco((previo) => (previo.resultado === resultado && previo.fila === fila ? previo : { resultado, fila }))
    }

    // Una barra de pocos casos mide un par de píxeles al lado de una de 60: casi imposible acertarle. Por eso
    // TODA la mitad de la franja del proveedor es clicable (la que se resalta). Un clic justo sobre una barra
    // lo atiende antes el `onClick` de la propia barra (burbujea hacia el gráfico) y esta función ya no hace
    // nada: el hook ignora la segunda llamada mientras hay una exportación en curso.
    const alClicFila = (estado) => {
        const fila = estado?.activePayload?.[0]?.payload
        if (!onExportar || !fila?.responsableId) return
        const resultado = mitadDe(estado)
        if (!cantidadDe(fila, resultado)) return
        onExportar(fila.responsableId, resultado)
    }

    return (
        <div>
            {onExportar && (
                <AyudaExportar texto='Clic en una barra, o en su zona resaltada, para descargar sus casos en Excel' />
            )}
            <ResponsiveContainer width='100%' height={altura}>
                <BarChart
                    data={data}
                    layout='vertical'
                    margin={{ top: 5, right: 60, left: 10, bottom: 5 }}
                    onClick={alClicFila}
                    onMouseMove={alMover}
                    style={{ cursor }}
                >
                    <CartesianGrid strokeDasharray='3 3' horizontal={false} />
                    <XAxis type='number' tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis type='category' dataKey='responsable' tick={{ fontSize: 11 }} width={160} />
                    <Tooltip
                        cursor={
                            onExportar
                                ? <MitadResaltada resultado={foco.resultado} cantidad={cantidadDe(foco.fila, foco.resultado)} />
                                : undefined
                        }
                        formatter={(value, name) => [value, NOMBRES_SERIE[name] || name]}
                        contentStyle={{ fontSize: 12 }}
                    />
                    <Legend formatter={(value) => NOMBRES_SERIE[value] || value} wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey='cerradosConRecupero' fill='#3a7d44' radius={[0, 3, 3, 0]} cursor={cursor} onClick={alClic('con')}>
                        <LabelList dataKey='cerradosConRecupero' position='right' style={{ fontSize: 10, fill: '#333' }} />
                    </Bar>
                    <Bar dataKey='cerradosSinRecupero' fill='#c0392b' radius={[0, 3, 3, 0]} cursor={cursor} onClick={alClic('sin')}>
                        <LabelList dataKey='cerradosSinRecupero' position='right' style={{ fontSize: 10, fill: '#333' }} />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    )
}

export default CerradosPorProveedor
